import { makeAutoObservable, observable } from 'mobx';

// PSTUDIO-3723: the generic undo/redo history engine, extracted verbatim from
// `tableStore` (behavior-preserving). It owns ONLY the two command stacks and
// their bookkeeping — it is domain-agnostic and knows nothing about rows,
// GraphQL, or validation. Command AUTHORING (snapshotting a row's before/after,
// wrapping a group mutation) stays in the store; this class just records the
// resulting commands and drives them on undo/redo.
//
// Why a separate module: the stack logic is pure and was buried in a ~10k-line
// store, so it could not be unit-tested in isolation. This mirrors how every
// mainstream undo library (mobx-state-tree `UndoManager`, zundo, travels) models
// the engine as its own manager, and gives a clean seam for a future history
// panel / serializable history without touching the ~20 command call sites.

// A single reversible operation. Both `execute` and `undo` are imperative
// closures that own their own side-effects (incl. persistence) — the manager
// never re-implements them, it only calls them in the right order. `focusTarget`
// is opaque to the manager (generic `F`); it is handed back to the injected
// `onReveal` callback so the host can reveal the affected cell after undo/redo.
export interface Command<F = unknown> {
  execute: () => void;
  undo: () => void;
  description: string;
  focusTarget?: F;
  // PSTUDIO-3845: after an undo/redo in AUTO validation mode, the host relies on
  // the command's OWN save path to recompile validation (a full-row save runs
  // validateAndSync). A command whose save path is validation-free — reorder,
  // which persists via the lightweight orders-only mutation — sets this `false`
  // so the host knows it must force the recompile itself. Defaults to `true`
  // (the common case: full-row saves already recompile), so existing commands
  // are unaffected.
  //
  // Contract for adding a new command: set `false` for ANY command whose
  // persistence does NOT recompile AND whose change CAN affect validation. The
  // default-`true` is safe for the current commands either because their save
  // path recompiles (cell edit, group mutation) OR because their change
  // is validation-irrelevant (pin/wrap/sort/expand — pure view state). A future
  // command that mutates node content but persists via a validation-free path
  // would silently reproduce the PSTUDIO-3845 staleness bug if it omits this.
  //
  // A command whose two legs persist ASYMMETRICALLY — one leg recompiles, the
  // other is validation-free — supplies a PREDICATE of the drive direction
  // instead of a static boolean, so each leg is gated independently:
  //   - insert (above/below): redo runs a full-row save (recompiles), undo runs
  //     an orders-only re-grid (does not) → `(dir) => dir === 'redo'`.
  //   - paste: each leg recompiles only when it re-creates rows via the full-row
  //     save (copy-paste redo; replace-paste undo), and is validation-free when
  //     it only removes rows + re-applies orders (cut+paste MOVE both legs;
  //     copy-paste undo) → gate on whichever row list that leg saves.
  recompilesViaSavePath?: boolean | ((direction: HistoryDirection) => boolean);
}

// Direction a command is driven in by the history engine. Handed to a
// direction-aware `recompilesViaSavePath` predicate so a command whose two
// legs persist through DIFFERENT paths (e.g. insert: redo runs a full-row
// save that recompiles, undo runs an orders-only re-grid that does not) can
// answer per-leg instead of with a single static boolean.
export type HistoryDirection = 'undo' | 'redo';

// Opaque handle returned by `checkpoint()` and consumed by `rollbackTo()`: the
// command that was on top of the undo stack at capture time (or `null` if the
// stack was empty). Compared by identity, never by index.
export type Checkpoint<F = unknown> = Command<F> | null;

export interface UndoRedoManagerOptions<F> {
  /** Cap on the undo stack; the oldest command is dropped past this. */
  maxHistorySize?: number;
  /** Called after undo()/redo() with the driven command's focus target. */
  onReveal?: (focusTarget?: F) => void;
}

export class UndoRedoManager<F = unknown> {
  // Public + readonly: the arrays themselves are the backward-compat surface the
  // store re-exposes as `tableStore.undoStack` / `redoStack` (tests reset them
  // with `.replace([])` and assert `.length`). `readonly` blocks reassignment;
  // the observable array is still mutable via its own methods.
  readonly undoStack = observable.array<Command<F>>([]);
  readonly redoStack = observable.array<Command<F>>([]);
  private readonly maxHistorySize: number;
  private readonly onReveal?: (focusTarget?: F) => void;

  constructor(options: UndoRedoManagerOptions<F> = {}) {
    this.maxHistorySize = options.maxHistorySize ?? 100;
    this.onReveal = options.onReveal;
    makeAutoObservable<this, 'maxHistorySize' | 'onReveal'>(this, {
      maxHistorySize: false,
      onReveal: false,
    });
  }

  /** Execute a command's effect, then record it (clears the redo stack). */
  execute(command: Command<F>) {
    command.execute();
    this.push(command);
  }

  // Register a command whose effect has ALREADY been applied to the store (e.g.
  // live cell edits committed on blur, drag-reorders applied while the user
  // dragged). Identical bookkeeping to execute() but skips the initial
  // execute() so we don't redundantly re-apply and re-save the change that just
  // happened. undo()/redo() still drive the command symmetrically.
  push(command: Command<F>) {
    this.undoStack.push(command);
    // A new command invalidates the redo branch.
    this.redoStack.replace([]);
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }
  }

  // Returns the driven command (or `undefined` on an empty stack) so the host
  // can make a post-history decision that depends on WHICH command ran — e.g.
  // PSTUDIO-3845's Auto-mode recompile gate keyed on `recompilesViaSavePath`.
  undo(): Command<F> | undefined {
    const command = this.undoStack.pop();
    if (!command) return undefined;
    command.undo();
    this.redoStack.push(command);
    this.onReveal?.(command.focusTarget);
    return command;
  }

  redo(): Command<F> | undefined {
    const command = this.redoStack.pop();
    if (!command) return undefined;
    command.execute();
    this.undoStack.push(command);
    this.onReveal?.(command.focusTarget);
    return command;
  }

  /** Wipe both stacks — e.g. when the loaded solution version changes so
   *  commands can never reference rows from a different dataset. */
  clear() {
    this.undoStack.replace([]);
    this.redoStack.replace([]);
  }

  get canUndo() {
    return this.undoStack.length > 0;
  }

  get canRedo() {
    return this.redoStack.length > 0;
  }

  /** Current undo-stack depth — used to roll a composite mutation back to a
   *  known point (see `rollbackTo`). */
  get undoSize() {
    return this.undoStack.length;
  }

  /** Read-only view of the undo stack (oldest → newest), for a history UI. */
  get history(): ReadonlyArray<Command<F>> {
    return this.undoStack;
  }

  // Capture an opaque checkpoint (the command currently on top of the undo
  // stack, or `null` if empty) to later `rollbackTo`. Identity-based on purpose:
  // a numeric depth would desync once `push` starts evicting from the head at
  // `maxHistorySize`, because the stack length then stops growing even as new
  // commands are appended (PSTUDIO-3723).
  checkpoint(): Checkpoint<F> {
    return this.undoStack.length
      ? this.undoStack[this.undoStack.length - 1]
      : null;
  }

  // Discard every command pushed since `checkpoint` was captured, and clear the
  // redo branch. Used by composite mutations (e.g. a group edit) that run a
  // block which may push several sub-commands, then replace them with ONE atomic
  // command: capture `checkpoint()` before the block, `rollbackTo(cp)` after,
  // then `push` the single composite command (PSTUDIO-3159 / -2154 pattern).
  //
  // Robust to head-eviction at `maxHistorySize`: we locate the marker by
  // identity and splice everything AFTER it. If the marker was itself evicted
  // during the block (`indexOf` → -1), every pre-block command is already gone,
  // so `splice(0)` correctly drops the remaining sub-commands too.
  rollbackTo(checkpoint: Checkpoint<F>) {
    const markerIndex = checkpoint ? this.undoStack.indexOf(checkpoint) : -1;
    this.undoStack.splice(markerIndex + 1);
    this.redoStack.replace([]);
  }
}
